import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { COMM_DEPARTMENT_LABEL, formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import { problem } from '../bookings/util.js';

// Communications > Send a message: everything sent, newest first, with how
// many have read and acknowledged it. A row opens its page.
export default function Sent() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { api('/comms').then(setList, (e) => setError(problem(e))); }, []);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (list || []).filter((c) => !s || `${c.subject} ${c.audience} ${c.sentByName}`.toLowerCase().includes(s));
  }, [list, q]);

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Messages sent</h1>
            <p className="muted">Messages to engineers and staff: they get them in Messages in the app, with a notification. Open one to see who has read and acknowledged it, and to chase.</p>
          </div>
          <Link to="/comms/new" className="btn primary with-icon"><Plus size={18} aria-hidden="true" /> New message</Link>
        </div>
      </section>
      <section className="card">
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!list && !error && <p className="muted">Loading…</p>}
        {list && !list.length && <p className="muted">Nothing sent yet.</p>}
        {list && list.length > 0 && (
          <>
            <label className="search">
              <Search size={18} aria-hidden="true" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, who it went to, who sent it" aria-label="Search messages" />
            </label>
            <div className="table-scroll">
              <table className="table clickable">
                <thead>
                  <tr><th>Message</th><th>To</th><th>Sent</th><th>Read</th><th>Acknowledged</th></tr>
                </thead>
                <tbody>
                  {shown.map((c) => (
                    <tr key={c.id} tabIndex={0} onClick={() => navigate(`/comms/${c.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/comms/${c.id}`); }}>
                      <td>
                        <strong>{c.subject}</strong>
                        <div className="small muted">{COMM_DEPARTMENT_LABEL[c.department]}{c.priority === 'urgent' ? ' · Urgent' : ''}{c.editedAt ? ' · Corrected' : ''}</div>
                      </td>
                      <td>{c.audience}<div className="small muted">{c.recipients} {c.recipients === 1 ? 'person' : 'people'}</div></td>
                      <td>{formatDateTime(c.sentAt)}<div className="small muted">{c.sentByName}</div></td>
                      <td><Meter n={c.read} of={c.recipients} /></td>
                      <td>{c.requiresAck ? <Meter n={c.acked} of={c.recipients} /> : <span className="muted small">Not asked</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export function Meter({ n, of }) {
  const pct = of ? Math.round((n / of) * 100) : 0;
  return (
    <span className="meter" title={`${n} of ${of}`}>
      <span className="meter-bar"><span style={{ width: `${pct}%` }} className={pct === 100 ? 'full' : ''} /></span>
      <span className="small">{pct}% <span className="muted">({n}/{of})</span></span>
    </span>
  );
}
